import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Truck, Loader2, AlertTriangle } from 'lucide-react';
import {
  fetchFleets, createFleet, type FleetSummary,
} from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthStore } from '@/store/auth';
import formStyles from '@/components/ui/Form.module.css';

/**
 * Admin Fleet list (Phase 2 fleet platform).
 *
 * CPO operators (tenant_admin / super_admin) see all fleets in
 * their tenant. fleet_admin users see only fleets they're
 * assigned to — same component, server-side filter applies.
 *
 * MRR column is a Phase 3 placeholder; today it always renders
 * "—". Active sessions this month is computed server-side in
 * a follow-up; today the cell reads "—" too.
 */

interface FleetsListPageProps {
  onNavigate: (path: string) => void;
}

export function FleetsListPage({ onNavigate }: FleetsListPageProps) {
  useRecentRoute('Fleets');
  // Fleet Operators can't create fleets (no Tenant:create) and reach this
  // list with an empty set until they're assigned to a fleet — drive a
  // coherent empty-state + hide the create affordance for them.
  const isFleetAdmin = useAuthStore((s) => s.user?.role) === 'fleet_admin';
  const [createOpen, setCreateOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const fleetsQ = useQuery({
    queryKey: ['fleets'],
    queryFn: fetchFleets,
  });

  // A Fleet Operator with exactly one fleet has nothing to pick from,
  // so the list forwards them to that fleet's Dashboard tab (Warren's
  // 2026-09-16 ruling: the per-fleet stats view is their dashboard
  // until the post-cutover dashboardScope fix). App.tsx lands them
  // there directly when the ['fleets'] cache has already resolved;
  // this covers the cold login (the list mounts before the count is
  // known) and the nav's Dashboard + Fleets leaves, which both point
  // here. Zero or 2+ fleets render the list as before.
  //
  // The forward REPLACES the list's history entry rather than pushing
  // on top of it. With a push, "Back to fleets" on the detail would
  // push /fleets, this effect would push the detail again, and browser
  // Back would bounce forever. App.tsx re-reads location.pathname on
  // popstate, so a synthetic popstate after replaceState is how a
  // route change is made here without a push (navigate() only pushes).
  const singleFleetId = isFleetAdmin && fleetsQ.data?.length === 1
    ? fleetsQ.data[0]?._id ?? null
    : null;
  useEffect(() => {
    if (!singleFleetId) return;
    window.history.replaceState({}, '', `/fleets/${singleFleetId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [singleFleetId]);

  if (singleFleetId) {
    return (
      <div style={{ padding: '24px 28px', color: 'var(--color-tx-m)' }}>
        <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} className="spin" /> Opening your fleet…
      </div>
    );
  }

  const rows = (fleetsQ.data ?? []).filter((f) => {
    if (activeFilter === 'active') return f.is_active;
    if (activeFilter === 'inactive') return !f.is_active;
    return true;
  });

  const columns: Column<FleetSummary>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (f) => (
        <a
          href={`/fleets/${f._id}`}
          onClick={(e) => {
            e.preventDefault();
            onNavigate(`/fleets/${f._id}`);
          }}
          style={{ color: 'var(--evi-accent, #2ECC71)', fontWeight: 600 }}
        >
          {f.name}
        </a>
      ),
    },
    { key: 'billing_email', header: 'Billing email', cell: (f) => f.billing_email },
    {
      key: 'subscription_tier',
      header: 'Plan',
      cell: () => '—', // Phase 3
    },
    {
      key: 'mrr',
      header: 'MRR',
      cell: () => '—', // Phase 3
    },
    {
      key: 'is_active',
      header: 'Status',
      cell: (f) =>
        f.is_active
          ? <span style={{ color: 'var(--evi-success, #10b981)' }}>Active</span>
          : <span style={{ color: 'var(--color-tx-m)' }}>Inactive</span>,
    },
  ];

  return (
    <div style={{ padding: '24px 28px' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Truck size={20} style={{ color: 'var(--evi-accent, #2ECC71)' }} />
          <h1 style={{ margin: 0, fontSize: 22 }}>Fleets</h1>
        </div>
        {!isFleetAdmin && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px',
              background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <Plus size={14} /> New fleet
          </button>
        )}
      </header>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['all', 'active', 'inactive'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setActiveFilter(k)}
            style={{
              padding: '6px 12px',
              background: activeFilter === k ? 'var(--color-sf2, #1a1a1a)' : 'transparent',
              color: 'var(--color-tx)',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 999,
              fontSize: 12,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {k}
          </button>
        ))}
      </div>

      {fleetsQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>
          <Loader2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} className="spin" /> Loading fleets…
        </p>
      )}
      {fleetsQ.isError && (
        <p style={{ color: 'var(--evi-error, #ef4444)' }}>
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Failed to load fleets.
        </p>
      )}
      {!fleetsQ.isLoading && !fleetsQ.isError && rows.length === 0 && (
        isFleetAdmin ? (
          <div style={{ color: 'var(--color-tx-m)', maxWidth: 540, lineHeight: 1.6 }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600, color: 'var(--color-tx)' }}>
              No fleet assigned yet
            </p>
            <p style={{ margin: 0 }}>
              You&rsquo;re set up as a fleet admin, but no fleet has been
              assigned to you yet. Whoever invited you grants that from the
              fleet&rsquo;s <strong>Admins</strong> tab — once you&rsquo;re added
              there, that fleet appears here.
            </p>
          </div>
        ) : (
          <p style={{ color: 'var(--color-tx-m)' }}>No fleets yet. Create one to get started.</p>
        )
      )}
      {rows.length > 0 && (
        <ResponsiveTable<FleetSummary>
          rows={rows}
          columns={columns}
          rowKey={(f) => f._id}
        />
      )}

      {createOpen && (
        <CreateFleetModal
          onClose={() => setCreateOpen(false)}
          onCreated={(fleet) => {
            setCreateOpen(false);
            onNavigate(`/fleets/${fleet._id}`);
          }}
        />
      )}
    </div>
  );
}

function CreateFleetModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (fleet: FleetSummary) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pm, setPm] = useState('');
  const [promo, setPromo] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      createFleet({
        name: name.trim(),
        billing_email: email.trim(),
        ...(pm.trim() ? { initial_payment_method_id: pm.trim() } : {}),
        ...(promo.trim() ? { promo_code: promo.trim() } : {}),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['fleets'] });
      onCreated(data);
    },
    onError: (e: Error) => setErr(e.message || 'Failed to create fleet'),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 92vw)',
          background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12,
          padding: 20,
        }}
      >
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>New fleet</h2>
        <label className={formStyles.label}>Name</label>
        <input
          className={formStyles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Logistics"
        />
        <label className={formStyles.label} style={{ marginTop: 10 }}>Billing email</label>
        <input
          className={formStyles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="billing@acme.com"
        />
        <label className={formStyles.label} style={{ marginTop: 10 }}>Initial payment method (optional)</label>
        <input
          className={formStyles.input}
          value={pm}
          onChange={(e) => setPm(e.target.value)}
          placeholder="pm_..."
        />
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
          Paste a Stripe payment-method id created via the Stripe Dashboard.
          Leave empty to add a card later from the Billing tab.
        </p>
        <label className={formStyles.label} style={{ marginTop: 10 }}>Promo code (optional)</label>
        <input
          className={formStyles.input}
          value={promo}
          onChange={(e) => setPromo(e.target.value)}
          placeholder="SWITCH2EVI"
        />
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 6, lineHeight: 1.5 }}>
          A Stripe promotion code. Validated at create; an invalid or expired code is rejected.
        </p>
        {err && (
          <div style={{
            marginTop: 10, padding: '8px 10px',
            color: 'var(--evi-error, #ef4444)',
            background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)',
            borderRadius: 6, fontSize: 12,
          }}>{err}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            style={{
              padding: '8px 14px', background: 'transparent',
              color: 'var(--color-tx)',
              border: '1px solid var(--color-brd, var(--evi-border))',
              borderRadius: 8, fontSize: 13, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setErr(null);
              if (!name.trim() || !email.trim()) {
                setErr('Name and billing email are required.');
                return;
              }
              mut.mutate();
            }}
            disabled={mut.isPending || !name.trim() || !email.trim()}
            style={{
              padding: '8px 14px',
              background: 'var(--evi-accent, #2ECC71)',
              color: '#000', border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {mut.isPending ? 'Creating…' : 'Create fleet'}
          </button>
        </div>
      </div>
    </div>
  );
}
